import { 
    Button, 
    TextField, 
    Typography, 
    Container, 
    Card, 
    CardContent, 
    Box, 
    Alert,
    Paper
} from '@mui/material';
import { DeleteForever, Email } from '@mui/icons-material';
import React, { useState } from 'react';
import { API_CONFIG } from '../configuration/api';

const Delete: React.FC = () => {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isSuccess, setIsSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleDeleteRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        setIsSuccess(false);

        try {
            const response = await fetch(API_CONFIG.getDataUrl(false), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    email: email.trim(),
                    action: 'delete_request'
                }),
            });

            if (response.ok) {
                setMessage('Your data deletion request has been submitted successfully. You will receive a confirmation email shortly.');
                setIsSuccess(true);
                setEmail(''); // Clear the form
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const errorData = await response.json();
                setMessage('There was an error submitting your request. Please contact us directly at privacy@famousinitials.com to delete your data.');
                setIsSuccess(false);
            }
        } catch (error) {
            setMessage('There was an error submitting your request. Please check your internet connection and try again, or contact us at privacy@famousinitials.com.');
            setIsSuccess(false);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Container maxWidth="md" sx={{ py: { xs: 0.25, sm: 0.2, md: 1.5 }, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
            <Typography variant="h3" component="h1" gutterBottom align="center" color="primary">
                <DeleteForever sx={{ fontSize: 'inherit', mr: 1, verticalAlign: 'text-bottom' }} />
                Delete Your Data
            </Typography>
            
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 4 }}>
                Request permanent deletion of your personal data from Famous Initials
            </Typography>

            <Card elevation={3} sx={{ maxWidth: 600, mx: 'auto' }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        If you would like to delete your personal data from our systems, please enter your email address below. 
                        We will process your request and send you a confirmation once your data has been removed.
                    </Typography>

                    <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                        <Typography variant="body2">
                            <strong>Important:</strong> This action cannot be undone. All your game progress, 
                            account information, and associated data will be permanently deleted.
                        </Typography>
                    </Paper>

                    <Box component="form" onSubmit={handleDeleteRequest} sx={{ mt: 2 }}>
                        <TextField
                            label="Email Address"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            fullWidth
                            margin="normal"
                            disabled={isLoading}
                            InputProps={{
                                startAdornment: <Email sx={{ mr: 1, color: 'action.active' }} />
                            }}
                            helperText="Enter the email address associated with your Famous Initials account"
                        />
                        
                        <Button 
                            type="submit" 
                            variant="contained" 
                            color="error"
                            size="large"
                            fullWidth
                            disabled={isLoading || !email.trim()}
                            sx={{ mt: 3, mb: 2 }}
                        >
                            {isLoading ? 'Submitting Request...' : 'Submit Deletion Request'}
                        </Button>
                    </Box>

                    {message && (
                        <Alert 
                            severity={isSuccess ? 'success' : 'error'} 
                            sx={{ mt: 2 }}
                        >
                            {message}
                        </Alert>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                        Need help? Contact us at{' '}
                        <Typography component="a" href="mailto:privacy@famousinitials.com" color="primary">
                            privacy@famousinitials.com
                        </Typography>
                    </Typography>
                </CardContent>
            </Card>
        </Container>
    );
};

export default Delete;